import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
  Grid,
} from '@mui/material';
import { Language as SiteIcon } from '@mui/icons-material';
import { FoodResponseDto } from '../../../services/food.service';
import {
  adminFoodService,
  UpsertFoodSiteContentDto,
} from '../../../services/admin-food.service';
import { toastService } from '../../../services/toast.service';

interface Props {
  open: boolean;
  onClose: () => void;
  food: FoodResponseDto | null;
  onPublishToggled: (foodId: number, newValue: boolean) => void;
}

const DIFFICULTY_OPTIONS: { value: 'easy' | 'medium' | 'hard'; label: string }[] = [
  { value: 'easy', label: 'Легко' },
  { value: 'medium', label: 'Середньо' },
  { value: 'hard', label: 'Складно' },
];

function NutrientPreview({ food, portionWeight }: { food: FoodResponseDto; portionWeight: number }) {
  if (!portionWeight || portionWeight <= 0) return null;
  const f = portionWeight / 100;
  const kcal = food.caloriesPer100g != null ? Math.round(food.caloriesPer100g * f) : null;
  const p = food.proteinPer100g != null ? parseFloat((Number(food.proteinPer100g) * f).toFixed(1)) : null;
  const fat = food.fatPer100g != null ? parseFloat((Number(food.fatPer100g) * f).toFixed(1)) : null;
  const c = food.carbsPer100g != null ? parseFloat((Number(food.carbsPer100g) * f).toFixed(1)) : null;

  if (kcal == null && p == null) return null;

  return (
    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
      <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
        Нутрієнти на порцію ({portionWeight} г):
      </Typography>
      <Box sx={{ display: 'flex', gap: 3 }}>
        {kcal != null && <Box sx={{ textAlign: 'center' }}><Typography variant="body2" fontWeight={600}>{kcal}</Typography><Typography variant="caption" color="text.secondary">ккал</Typography></Box>}
        {p != null && <Box sx={{ textAlign: 'center' }}><Typography variant="body2" fontWeight={600}>{p}</Typography><Typography variant="caption" color="text.secondary">білки</Typography></Box>}
        {fat != null && <Box sx={{ textAlign: 'center' }}><Typography variant="body2" fontWeight={600}>{fat}</Typography><Typography variant="caption" color="text.secondary">жири</Typography></Box>}
        {c != null && <Box sx={{ textAlign: 'center' }}><Typography variant="body2" fontWeight={600}>{c}</Typography><Typography variant="caption" color="text.secondary">вуглев.</Typography></Box>}
      </Box>
    </Box>
  );
}

export const FoodSiteContentDialog: React.FC<Props> = ({ open, onClose, food, onPublishToggled }) => {
  const [isPublished, setIsPublished] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);

  const [contentLoading, setContentLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [prepareTime, setPrepareTime] = useState<string>('');
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard' | ''>('');
  const [portionWeight, setPortionWeight] = useState<string>('');
  const [ingredientsText, setIngredientsText] = useState('');
  const [recipeText, setRecipeText] = useState('');

  useEffect(() => {
    if (!open || !food) return;
    setIsPublished(food.isSitePublished ?? false);
    loadContent();
  }, [open, food?.id]);

  const loadContent = async () => {
    if (!food) return;
    setContentLoading(true);
    try {
      const data = await adminFoodService.getSiteContent(food.id);
      setPrepareTime(data?.prepareTimeMinutes?.toString() ?? '');
      setDifficulty(data?.difficulty ?? '');
      setPortionWeight(data?.portionWeightG?.toString() ?? '');
      setIngredientsText(data?.ingredientsText ?? '');
      setRecipeText(data?.recipeText ?? '');
    } catch {
      toastService.error('Не вдалося завантажити контент для сайту');
    } finally {
      setContentLoading(false);
    }
  };

  const handleTogglePublish = async () => {
    if (!food) return;
    setPublishLoading(true);
    try {
      const res = await adminFoodService.toggleSitePublished(food.id);
      setIsPublished(res.isSitePublished);
      onPublishToggled(food.id, res.isSitePublished);
      toastService.success(res.isSitePublished ? 'Страву опубліковано на сайті' : 'Страву знято з публікації');
    } catch {
      toastService.error('Не вдалося змінити статус публікації');
    } finally {
      setPublishLoading(false);
    }
  };

  const handleSave = async () => {
    if (!food) return;
    setSaving(true);
    try {
      const payload: UpsertFoodSiteContentDto = {};
      if (prepareTime !== '') payload.prepareTimeMinutes = parseInt(prepareTime) || null;
      if (difficulty !== '') payload.difficulty = difficulty;
      if (portionWeight !== '') payload.portionWeightG = parseInt(portionWeight) || null;
      if (ingredientsText !== '') payload.ingredientsText = ingredientsText;
      if (recipeText !== '') payload.recipeText = recipeText;

      await adminFoodService.upsertSiteContent(food.id, payload);
      toastService.success('Контент для сайту збережено');
    } catch {
      toastService.error('Не вдалося зберегти контент');
    } finally {
      setSaving(false);
    }
  };

  if (!food) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SiteIcon color="primary" />
        <Box>
          <Typography variant="h6">Публікація на сайті</Typography>
          <Typography variant="caption" color="text.secondary">{food.name}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Publish toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'background.default', borderRadius: 1, mb: 3 }}>
          <Box>
            <Typography variant="body2" fontWeight={600}>Публікація на everyday.ua</Typography>
            <Typography variant="caption" color="text.secondary">
              {isPublished ? 'Страва відображається для відвідувачів' : 'Страва прихована від відвідувачів'}
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={isPublished}
                onChange={handleTogglePublish}
                disabled={publishLoading}
                color="success"
              />
            }
            label={isPublished ? 'Опубліковано' : 'Не опубліковано'}
            labelPlacement="start"
          />
        </Box>

        <Divider sx={{ mb: 3 }}>
          <Typography variant="caption" color="text.secondary">Контент для сайту</Typography>
        </Divider>

        {contentLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Час приготування (хв)"
                type="number"
                value={prepareTime}
                onChange={(e) => setPrepareTime(e.target.value)}
                placeholder="30"
                fullWidth
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Складність</InputLabel>
                <Select
                  value={difficulty}
                  label="Складність"
                  onChange={(e) => setDifficulty(e.target.value as typeof difficulty)}
                >
                  <MenuItem value=""><em>— не вказано —</em></MenuItem>
                  {DIFFICULTY_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Вага порції (г)"
                type="number"
                value={portionWeight}
                onChange={(e) => setPortionWeight(e.target.value)}
                placeholder="300"
                fullWidth
                inputProps={{ min: 0 }}
              />
              <NutrientPreview food={food} portionWeight={parseInt(portionWeight) || 0} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Інгредієнти"
                multiline
                rows={4}
                value={ingredientsText}
                onChange={(e) => setIngredientsText(e.target.value)}
                placeholder="Перерахуйте інгредієнти..."
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Рецепт (для сайту)"
                multiline
                rows={5}
                value={recipeText}
                onChange={(e) => setRecipeText(e.target.value)}
                placeholder="Залиште порожнім, щоб використати стандартний опис страви..."
                fullWidth
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">Закрити</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || contentLoading}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Зберегти контент
        </Button>
      </DialogActions>
    </Dialog>
  );
};
